import React, { useEffect } from 'react';
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import { Bold, Italic, Underline, Heading2, Heading3, List, ListOrdered, Link2, Quote, Minus, Undo2, Redo2, Pilcrow } from 'lucide-react';

const Btn = ({ active, onClick, title, testid, children }) => (
  <button
    type="button"
    title={title}
    data-testid={testid}
    onMouseDown={(e) => e.preventDefault()}
    onClick={onClick}
    className={`p-1.5 rounded-md transition-colors ${active ? 'bg-emerald-600 text-white' : 'text-neutral-600 dark:text-neutral-300 hover:bg-neutral-100 dark:hover:bg-neutral-800'}`}
  >
    {children}
  </button>
);

const RichTextEditor = ({ value, onChange }) => {
  const editor = useEditor({
    extensions: [StarterKit.configure({ link: { openOnClick: false, autolink: true } })],
    content: value || '',
    onUpdate: ({ editor: ed }) => onChange(ed.getHTML()),
    editorProps: { attributes: { class: 'rich-content min-h-[260px] px-4 py-3 outline-none', 'data-testid': 'page-editor-content' } },
  });

  useEffect(() => {
    if (editor && value !== editor.getHTML()) editor.commands.setContent(value || '', { emitUpdate: false });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editor]);

  if (!editor) return null;
  const c = () => editor.chain().focus();
  const setLink = () => {
    const prev = editor.getAttributes('link').href || '';
    const url = window.prompt('Link URL (https://, mailto:, tel:) — leave empty to remove', prev);
    if (url === null) return;
    if (!url.trim()) c().extendMarkRange('link').unsetLink().run();
    else c().extendMarkRange('link').setLink({ href: url.trim() }).run();
  };

  return (
    <div className="rounded-lg border border-neutral-200 dark:border-neutral-800 bg-white dark:bg-[#0f0f10] overflow-hidden" data-testid="rich-text-editor">
      <div className="flex flex-wrap items-center gap-0.5 px-2 py-1.5 border-b border-neutral-200 dark:border-neutral-800 bg-neutral-50 dark:bg-[#141416]">
        <Btn testid="rte-paragraph" title="Paragraph" active={editor.isActive('paragraph')} onClick={() => c().setParagraph().run()}><Pilcrow size={15} /></Btn>
        <Btn testid="rte-h2" title="Heading" active={editor.isActive('heading', { level: 2 })} onClick={() => c().toggleHeading({ level: 2 }).run()}><Heading2 size={15} /></Btn>
        <Btn testid="rte-h3" title="Subheading" active={editor.isActive('heading', { level: 3 })} onClick={() => c().toggleHeading({ level: 3 }).run()}><Heading3 size={15} /></Btn>
        <span className="w-px h-5 bg-neutral-200 dark:bg-neutral-700 mx-1" />
        <Btn testid="rte-bold" title="Bold" active={editor.isActive('bold')} onClick={() => c().toggleBold().run()}><Bold size={15} /></Btn>
        <Btn testid="rte-italic" title="Italic" active={editor.isActive('italic')} onClick={() => c().toggleItalic().run()}><Italic size={15} /></Btn>
        <Btn testid="rte-underline" title="Underline" active={editor.isActive('underline')} onClick={() => c().toggleUnderline().run()}><Underline size={15} /></Btn>
        <Btn testid="rte-link" title="Link" active={editor.isActive('link')} onClick={setLink}><Link2 size={15} /></Btn>
        <span className="w-px h-5 bg-neutral-200 dark:bg-neutral-700 mx-1" />
        <Btn testid="rte-bullet-list" title="Bullet list" active={editor.isActive('bulletList')} onClick={() => c().toggleBulletList().run()}><List size={15} /></Btn>
        <Btn testid="rte-ordered-list" title="Numbered list" active={editor.isActive('orderedList')} onClick={() => c().toggleOrderedList().run()}><ListOrdered size={15} /></Btn>
        <Btn testid="rte-quote" title="Quote" active={editor.isActive('blockquote')} onClick={() => c().toggleBlockquote().run()}><Quote size={15} /></Btn>
        <Btn testid="rte-hr" title="Divider" onClick={() => c().setHorizontalRule().run()}><Minus size={15} /></Btn>
        <span className="flex-1" />
        <Btn testid="rte-undo" title="Undo" onClick={() => c().undo().run()}><Undo2 size={15} /></Btn>
        <Btn testid="rte-redo" title="Redo" onClick={() => c().redo().run()}><Redo2 size={15} /></Btn>
      </div>
      <EditorContent editor={editor} />
    </div>
  );
};

export default RichTextEditor;
